import {
  checkPlaylistTitle,
  formatRoute,
  loginRoute,
  parseRoute,
  pickImage,
  playlistIsReachableByLink,
  playlistVisibility,
  PLAYLIST_TITLE_MAX_BYTES,
  retargetVideoFrameUrl,
  utf8ByteLength,
  type OwnedPlaylistsNote,
  type Playlist,
  type PlaylistMembership,
  type PlaylistPrivacy,
  type PlaylistVisibility,
} from '@visious/core';
import { describeAuthProblem, type ProblemCopy } from '../api/problem';
import type { AuthApiProblem } from '../api/auth';
import { fmt, text } from '../strings';

/**
 * The reasoning shared by everything that touches a playlist the reader owns — §8's Playlists page, the
 * editing half of the playlist page, and the add-to-playlist menu on Watch.
 *
 * Its own directory rather than a screen's `model.ts` for `listing/`'s reason: three callers arrived in
 * the same slice, and the alternative is the same visibility copy and the same title check written three
 * times. R13 still holds — this file has no DOM types and no React, and the `.tsx` beside it renders
 * what it returns.
 */

/* ------------------------------------------------------------------------------------------------
 * Visibility, in words
 * ---------------------------------------------------------------------------------------------- */

export interface VisibilityCopy {
  /** What to show as the current state. */
  readonly label: string;
  /** Why it is not more specific, or `null` when it is exact. */
  readonly caveat: string | null;
  /** The tooltip either way — the caveat when there is one, otherwise what the setting means. */
  readonly title: string;
}

/**
 * The three-values-in, two-values-out problem, said out loud.
 *
 * `isListed` is a boolean over three privacy settings, and no response carries `privacy` at all — so a
 * playlist this session did not just write is either *definitely* public or *one of* unlisted and
 * private. The label states whichever of those is true rather than picking one and hoping, because the
 * difference is whether a link the reader sent somebody works.
 */
export function describeVisibility(visibility: PlaylistVisibility): VisibilityCopy {
  const words = text().playlists.visibility;
  if (visibility.privacy !== null) {
    return {
      label: privacyLabel()[visibility.privacy],
      caveat: null,
      title: privacyDetail()[visibility.privacy],
    };
  }
  if (visibility.listed === 'listed') {
    return { label: words.public, caveat: null, title: words.detail.public };
  }
  return { label: words.notPublic, caveat: words.unknownCaveat, title: words.unknownCaveat };
}

/**
 * Functions rather than constants, which is the one shape change the catalogue forced on this file: a
 * module-level `Record` freezes whichever language was active when the module was first imported, and
 * these two are read by three screens on every render.
 */
export function privacyLabel(): Readonly<Record<PlaylistPrivacy, string>> {
  const words = text().playlists.visibility;
  return { public: words.public, unlisted: words.unlisted, private: words.private };
}

export function privacyDetail(): Readonly<Record<PlaylistPrivacy, string>> {
  return text().playlists.visibility.detail;
}

/* ------------------------------------------------------------------------------------------------
 * A row of §8's Playlists page
 * ---------------------------------------------------------------------------------------------- */

/**
 * What the card's 16:9 frame draws (the collage): the first entries' own thumbnails when the
 * embedded page carries at least two, one alone when that is all there is, and the frame's glyph when
 * the playlist is empty. Built from `entries` — the accident of how the list arrives (up to 100
 * embedded per playlist, §7i) put the exact right data in our hands for free.
 */
export type PlaylistCover =
  | { readonly kind: 'none' }
  | { readonly kind: 'single'; readonly url: string }
  | { readonly kind: 'collage'; readonly urls: readonly string[] };

export interface OwnedPlaylistRow {
  readonly playlistId: string;
  readonly href: string;
  /** Never empty: the API will happily store `""`, so a row with no title says so rather than showing nothing. */
  readonly title: string;
  readonly untitled: boolean;
  /** Already pluralised — "1 video" / "23 videos". See `Formatters.countedNoun`. */
  readonly countText: string | null;
  readonly visibility: VisibilityCopy;
  /** `null` when the API will not say — see {@link describeVisibility}. */
  readonly linkWorks: boolean | null;
  readonly cover: PlaylistCover;
  readonly updatedText: string | null;
}

/** Half a ~300 px card at 2× wants ~300 source pixels; `mqdefault` (320) is the smallest that covers. */
const COLLAGE_TILE_MIN_WIDTH = 300;
const COLLAGE_TILES = 4;

function coverFor(playlist: Playlist): PlaylistCover {
  const urls: string[] = [];
  const seen = new Set<string>();
  for (const entry of playlist.entries) {
    // Distinct videos only — a playlist that repeats one video should not tile one picture four times.
    if (entry.video.videoId === '' || seen.has(entry.video.videoId)) continue;
    seen.add(entry.video.videoId);
    const url = pickImage(entry.video.thumbnails, {
      minWidth: COLLAGE_TILE_MIN_WIDTH,
      widescreen: true,
    })?.url;
    if (url !== undefined) urls.push(url);
    if (urls.length === COLLAGE_TILES) break;
  }

  if (urls.length >= 2) return { kind: 'collage', urls };
  const single = urls[0];
  if (single !== undefined) return { kind: 'single', url: single };
  // The record's own art, where there is any — a native playlist carries none, which is `none`.
  return playlist.thumbnail === null
    ? { kind: 'none' }
    : { kind: 'single', url: retargetVideoFrameUrl(playlist.thumbnail.url, 'mqdefault') };
}

export function toOwnedPlaylistRow(
  playlist: Playlist,
  known: PlaylistPrivacy | null,
  updatedText: string | null,
): OwnedPlaylistRow {
  const visibility = playlistVisibility(playlist, known);
  const title = playlist.title.trim();

  return {
    playlistId: playlist.playlistId,
    href: formatRoute({ kind: 'playlist', playlistId: playlist.playlistId }),
    title: title === '' ? text().playlists.untitled : playlist.title,
    untitled: title === '',
    /*
     * `videoCount` rather than `entries.length`, deliberately: the list endpoint embeds only the first
     * 100 entries, so a 340-entry playlist would read "100 videos" off the array. The count is the
     * field that is right, and `entries` is the accident of how it arrived.
     */
    countText: fmt().countedNoun(playlist.videoCount, text().playlists.video),
    visibility: describeVisibility(visibility),
    linkWorks: playlistIsReachableByLink(visibility),
    cover: coverFor(playlist),
    updatedText,
  };
}

/* ------------------------------------------------------------------------------------------------
 * The title editor's own state
 * ---------------------------------------------------------------------------------------------- */

export interface TitleFieldState {
  /** `null` when the value is fine. */
  readonly problem: string | null;
  /** "18 / 150 bytes" — shown only once it is worth knowing, so it is not noise on a short title. */
  readonly hint: string | null;
  /** What to send, or `null` when there is nothing valid to send. */
  readonly value: string | null;
}

/**
 * What to say about a title as it is typed.
 *
 * The byte counter is not pedantry: the cap is in **bytes**, so "76 characters" is over the limit in
 * German and under it in English, and the number that decides is one the reader cannot see. It appears
 * only past 60 % of the cap, because a counter on a three-word title is clutter that teaches nothing.
 */
export function titleFieldState(input: string): TitleFieldState {
  const words = text().playlists.title;
  const check = checkPlaylistTitle(input);
  const bytes = utf8ByteLength(input.trim());
  const hint =
    bytes > PLAYLIST_TITLE_MAX_BYTES * 0.6
      ? bytes > PLAYLIST_TITLE_MAX_BYTES
        ? words.bytesOver(bytes, PLAYLIST_TITLE_MAX_BYTES)
        : words.bytes(bytes, PLAYLIST_TITLE_MAX_BYTES)
      : null;

  if (!check.ok) {
    return {
      // Only shown once something has been typed: an empty field on arrival is not a mistake yet.
      problem: input === '' ? null : words.missing,
      hint,
      value: null,
    };
  }
  return { problem: null, hint, value: check.title };
}

/* ------------------------------------------------------------------------------------------------
 * Membership, in words — the add-to-playlist menu
 * ---------------------------------------------------------------------------------------------- */

/** The row shape lives in `addModel.ts` beside the function that builds it; this is the copy half. */
export function membershipNote(membership: PlaylistMembership): string | null {
  switch (membership) {
    case 'present':
      return text().playlists.membership.present;
    case 'unknown':
      // The honest sentence for the case the payload cannot settle. Saying nothing would read as
      // "not in it", which is how a reader ends up with a duplicate the endpoint happily accepts.
      return text().playlists.membership.unknown;
    case 'absent':
      return null;
  }
}

/* ------------------------------------------------------------------------------------------------
 * Notes and problems
 * ---------------------------------------------------------------------------------------------- */

export interface PlaylistsNotice {
  readonly id: string;
  readonly text: string;
}

/**
 * The one note that is about the reader's playlists rather than about the response.
 *
 * All three are silent now. `privacy-unreadable` was the last to go: the "Not public" badge
 * still carries its explanation as a hover title on the row itself, which is where a reader looking
 * at that word actually is. Core still counts every note — they are how the tests prove the endpoint.
 */
export function ownedNoticeFor(note: OwnedPlaylistsNote): PlaylistsNotice | null {
  switch (note.id) {
    case 'privacy-unreadable':
    case 'embeds-entries':
    case 'dropped':
      return null;
  }
}

/**
 * The two sentences that are about *your* playlists rather than about a playlist.
 *
 * `notFound` is reachable in a way the public page's is not: this list can be stale, so a row can point
 * at a playlist that has since been deleted — on this instance, from another tab, or by this app a
 * moment ago.
 */
export function ownedPlaylistsProblem(): {
  readonly notFound: ProblemCopy;
  readonly unreadable: () => ProblemCopy;
} {
  const words = text().playlists.problem;
  return { notFound: words.notFound, unreadable: () => words.unreadable };
}

/** The copy for a *write* that failed, which needs a noun the shared taxonomy does not have. */
export function describeWriteProblem(problem: AuthApiProblem, action: WriteAction): ProblemCopy {
  if (problem.kind === 'transport' && problem.error.kind === 'video-unavailable') {
    const copy = notFoundCopy()[problem.error.notFound ?? 'unknown'];
    if (copy !== undefined) return copy;
  }
  if (problem.kind === 'transport' && problem.error.kind === 'client') {
    /*
     * Every malformed-body refusal lands here — `Missing hash key: "title"`, `Unknown enum
     * PlaylistPrivacy value`, `invalid byte sequence for encoding "UTF8"`, `Invalid Int64` — all of them
     * `403`, all of them read as `client` by 3b's structural rule rather than as an expired session.
     *
     * Which makes them **our** bug, not the reader's, and the copy says so instead of suggesting they
     * sign in again. Every one is prevented upstream: `updatePlaylistBody` types `title` as required,
     * `PlaylistPrivacy` is a union, `clampPlaylistTitle` keeps the title inside 150 bytes and the
     * removal key is never normalised. So reaching this arm means one of those broke.
     */
    return text().playlists.write.badBody(text().playlists.action[action]);
  }
  return describeAuthProblem(problem, ownedPlaylistsProblem());
}

export type WriteAction = 'create' | 'update' | 'delete' | 'add' | 'remove';

function notFoundCopy(): Readonly<Record<string, ProblemCopy | undefined>> {
  const words = text().playlists.write;
  return {
    playlist: words.playlistGone,
    'playlist-entry': words.entryGone,
    video: words.videoGone,
  };
}

/**
 * `/login?next=<here>` — the signed-out arm of both playlist dialogs, which offer to sign a reader in
 * and bring them back to the page they were on. Parsed rather than hand-built, so it stays a fixed
 * point of `canonicalHref` (`safeNextHref`).
 *
 * The watch page builds its own instead (`useWatchScreen`), pointing at the video without the `t`:
 * a sign-in is a top-level round trip to the instance, long enough that coming back to the second the
 * reader left is a worse answer than coming back to the video.
 */
export function signInHereHref(): string {
  const here = `${globalThis.location.pathname}${globalThis.location.search}`;
  return formatRoute(loginRoute(parseRoute(here)));
}
